import { supabase } from "@/lib/supabase/client.ts";
import type { GeneratorSettings, LockState, StorySeed, StorySeedDrafts } from "@/types/story.ts";
import {
  DEFAULT_LOCK_STATE,
  DEFAULT_SETTINGS,
  EMPTY_DRAFTS,
  EMPTY_STORY_SEED,
} from "@/types/story.ts";

const STORAGE_KEY = "plotcat-saved-stories";
const STORIES_TABLE = "stories";
const PUBLISHED_STORIES_TABLE = "published_stories";
const MAX_LOCAL_STORIES = 50;

export interface SavedStory {
  id: string;
  name: string;
  timestamp: number;
  storySeed: StorySeed;
  drafts: StorySeedDrafts;
  lockState: LockState;
  settings: GeneratorSettings;
}

export interface PublishedStory {
  id: string;
  storyId: string;
  slug: string;
  title: string;
  theme: string;
  language: string;
  objectiveExcerpt: string;
  publishedAt: number;
  snapshot: PublishedStorySnapshot;
}

export interface PublishedStorySnapshot {
  id: string;
  name: string;
  timestamp: number;
  storySeed: StorySeed;
  lockState: LockState;
  settings: GeneratorSettings;
}

interface StoryRow {
  id: string;
  owner_id: string;
  name: string;
  story_seed: StorySeed;
  drafts: StorySeedDrafts;
  lock_state: LockState;
  settings: GeneratorSettings;
  updated_at: string;
}

interface PublishedStoryRow {
  id: string;
  story_id: string;
  slug: string;
  title: string;
  theme: string | null;
  language: string | null;
  objective_excerpt: string | null;
  snapshot: PublishedStorySnapshot;
  published_at: string;
}

function getLocalStories(): SavedStory[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

function saveLocalStories(stories: SavedStory[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stories.slice(0, MAX_LOCAL_STORIES)));
}

function mapStoryRow(row: StoryRow): SavedStory {
  return {
    id: row.id,
    name: row.name,
    timestamp: Date.parse(row.updated_at) || Date.now(),
    storySeed: row.story_seed,
    drafts: row.drafts,
    lockState: row.lock_state,
    settings: row.settings,
  };
}

function mapPublishedStoryRow(row: PublishedStoryRow): PublishedStory {
  return {
    id: row.id,
    storyId: row.story_id,
    slug: row.slug,
    title: row.title,
    theme: row.theme || "",
    language: row.language || "",
    objectiveExcerpt: row.objective_excerpt || "",
    publishedAt: Date.parse(row.published_at) || Date.now(),
    snapshot: row.snapshot,
  };
}

function toStoryInsert(story: SavedStory, ownerId: string) {
  return {
    id: story.id,
    owner_id: ownerId,
    name: story.name,
    story_seed: story.storySeed,
    drafts: story.drafts,
    lock_state: story.lockState,
    settings: story.settings,
  };
}

function slugify(input: string): string {
  const normalized = input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

  return normalized || "story";
}

async function getCurrentUserId(): Promise<string | null> {
  if (!supabase) return null;

  const { data, error } = await supabase.auth.getSession();
  if (error) {
    console.warn("[Storage] Failed to resolve current user:", error.message);
    return null;
  }

  return data.session?.user?.id ?? null;
}

export async function getSavedStories(): Promise<SavedStory[]> {
  const userId = await getCurrentUserId();

  if (supabase && userId) {
    const { data, error } = await supabase
      .from(STORIES_TABLE)
      .select("id, owner_id, name, story_seed, drafts, lock_state, settings, updated_at")
      .eq("owner_id", userId)
      .order("updated_at", { ascending: false });

    if (error) {
      console.warn("[Storage] Failed to load cloud stories, falling back to local:", error.message);
      return getLocalStories();
    }

    return (data as StoryRow[]).map(mapStoryRow);
  }

  return getLocalStories();
}

export async function getStoryById(id: string): Promise<SavedStory | null> {
  const userId = await getCurrentUserId();

  if (supabase && userId) {
    const { data, error } = await supabase
      .from(STORIES_TABLE)
      .select("id, owner_id, name, story_seed, drafts, lock_state, settings, updated_at")
      .eq("owner_id", userId)
      .eq("id", id)
      .maybeSingle();

    if (error) {
      console.warn("[Storage] Failed to fetch cloud story, falling back to local:", error.message);
    } else if (data) {
      return mapStoryRow(data as StoryRow);
    }
  }

  const stories = getLocalStories();
  return stories.find((s) => s.id === id) || null;
}

export async function createNewStory(name?: string): Promise<SavedStory> {
  const newStory: SavedStory = {
    id: crypto.randomUUID(),
    name: name || `Story ${new Date().toLocaleDateString()}`,
    timestamp: Date.now(),
    storySeed: EMPTY_STORY_SEED,
    drafts: EMPTY_DRAFTS,
    lockState: DEFAULT_LOCK_STATE,
    settings: DEFAULT_SETTINGS,
  };

  const userId = await getCurrentUserId();
  if (supabase && userId) {
    const { error } = await supabase
      .from(STORIES_TABLE)
      .insert(toStoryInsert(newStory, userId));

    if (error) {
      console.warn("[Storage] Failed to create cloud story, using local storage:", error.message);
      const stories = getLocalStories();
      stories.unshift(newStory);
      saveLocalStories(stories);
    }
    return newStory;
  }

  const stories = getLocalStories();
  stories.unshift(newStory);
  saveLocalStories(stories);
  return newStory;
}

export async function saveStory(story: SavedStory): Promise<void> {
  const userId = await getCurrentUserId();
  const updatedStory = { ...story, timestamp: Date.now() };

  if (supabase && userId) {
    const { error } = await supabase
      .from(STORIES_TABLE)
      .upsert(toStoryInsert(updatedStory, userId), { onConflict: "id" });

    if (!error) {
      return;
    }

    console.warn("[Storage] Failed to save cloud story, using local storage:", error.message);
  }

  const stories = getLocalStories();
  const index = stories.findIndex((s) => s.id === story.id);

  if (index >= 0) {
    stories[index] = updatedStory;
  } else {
    stories.unshift(updatedStory);
  }

  saveLocalStories(stories);
}

export async function deleteStory(id: string): Promise<void> {
  const userId = await getCurrentUserId();

  if (supabase && userId) {
    const { error } = await supabase
      .from(STORIES_TABLE)
      .delete()
      .eq("owner_id", userId)
      .eq("id", id);

    if (!error) {
      return;
    }

    console.warn("[Storage] Failed to delete cloud story, using local storage:", error.message);
  }

  const stories = getLocalStories();
  const filtered = stories.filter((s) => s.id !== id);
  saveLocalStories(filtered);
}

export async function renameStory(id: string, newName: string): Promise<void> {
  const userId = await getCurrentUserId();

  if (supabase && userId) {
    const { error } = await supabase
      .from(STORIES_TABLE)
      .update({ name: newName })
      .eq("owner_id", userId)
      .eq("id", id);

    if (!error) {
      return;
    }

    console.warn("[Storage] Failed to rename cloud story, using local storage:", error.message);
  }

  const stories = getLocalStories();
  const story = stories.find((s) => s.id === id);
  if (story) {
    story.name = newName;
    story.timestamp = Date.now();
    saveLocalStories(stories);
  }
}

export async function publishStory(storyId: string): Promise<PublishedStory> {
  if (!supabase) {
    throw new Error(
      "Supabase is not configured. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.",
    );
  }

  const userId = await getCurrentUserId();
  if (!userId) {
    throw new Error("You must be signed in to publish stories.");
  }

  const story = await getStoryById(storyId);
  if (!story) {
    throw new Error("Story not found.");
  }

  const baseSlug = slugify(story.storySeed.storyName || story.name);
  const slug = `${baseSlug}-${story.id.slice(0, 6)}-${Date.now().toString(36)}`;

  const snapshot: PublishedStorySnapshot = {
    id: story.id,
    name: story.name,
    timestamp: Date.now(),
    storySeed: story.storySeed,
    lockState: story.lockState,
    settings: story.settings,
  };

  const payload = {
    story_id: story.id,
    owner_id: userId,
    slug,
    title: story.storySeed.storyName || story.name,
    theme: story.settings.theme,
    language: story.settings.language,
    objective_excerpt: story.storySeed.mainObjective.description,
    snapshot,
  };

  const { data, error } = await supabase
    .from(PUBLISHED_STORIES_TABLE)
    .insert(payload)
    .select("id, story_id, slug, title, theme, language, objective_excerpt, snapshot, published_at")
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return mapPublishedStoryRow(data as PublishedStoryRow);
}

export async function getMyPublishedStories(storyId?: string): Promise<PublishedStory[]> {
  if (!supabase) return [];

  const userId = await getCurrentUserId();
  if (!userId) return [];

  let query = supabase
    .from(PUBLISHED_STORIES_TABLE)
    .select("id, story_id, slug, title, theme, language, objective_excerpt, snapshot, published_at")
    .eq("owner_id", userId)
    .order("published_at", { ascending: false });

  if (storyId) {
    query = query.eq("story_id", storyId);
  }

  const { data, error } = await query.limit(100);

  if (error) {
    console.warn("[Storage] Failed to load your published stories:", error.message);
    return [];
  }

  return (data as PublishedStoryRow[]).map(mapPublishedStoryRow);
}

export async function unpublishStory(publishedId: string): Promise<void> {
  if (!supabase) {
    throw new Error("Supabase is not configured.");
  }

  const userId = await getCurrentUserId();
  if (!userId) {
    throw new Error("You must be signed in to unpublish stories.");
  }

  const { error } = await supabase
    .from(PUBLISHED_STORIES_TABLE)
    .delete()
    .eq("id", publishedId)
    .eq("owner_id", userId);

  if (error) {
    throw new Error(error.message);
  }
}

export async function getPublishedStories(): Promise<PublishedStory[]> {
  if (!supabase) {
    return [];
  }

  const { data, error } = await supabase
    .from(PUBLISHED_STORIES_TABLE)
    .select("id, story_id, slug, title, theme, language, objective_excerpt, snapshot, published_at")
    .order("published_at", { ascending: false })
    .limit(100);

  if (error) {
    console.warn("[Storage] Failed to load published stories:", error.message);
    return [];
  }

  return (data as PublishedStoryRow[]).map(mapPublishedStoryRow);
}

export async function clonePublishedStory(snapshot: PublishedStorySnapshot): Promise<SavedStory> {
  const newStory: SavedStory = {
    id: crypto.randomUUID(),
    name: snapshot.name,
    timestamp: Date.now(),
    storySeed: snapshot.storySeed,
    drafts: EMPTY_DRAFTS,
    lockState: snapshot.lockState,
    settings: { ...snapshot.settings, generationStep: 4 },
  };

  const userId = await getCurrentUserId();
  if (supabase && userId) {
    const { error } = await supabase
      .from(STORIES_TABLE)
      .insert(toStoryInsert(newStory, userId));

    if (error) {
      console.warn("[Storage] Failed to clone story to cloud, using local storage:", error.message);
      const stories = getLocalStories();
      stories.unshift(newStory);
      saveLocalStories(stories);
    }
    return newStory;
  }

  const stories = getLocalStories();
  stories.unshift(newStory);
  saveLocalStories(stories);
  return newStory;
}

export async function getPublishedStoryBySlug(slug: string): Promise<PublishedStory | null> {
  if (!supabase) {
    return null;
  }

  const { data, error } = await supabase
    .from(PUBLISHED_STORIES_TABLE)
    .select("id, story_id, slug, title, theme, language, objective_excerpt, snapshot, published_at")
    .eq("slug", slug)
    .maybeSingle();

  if (error) {
    console.warn("[Storage] Failed to load published story:", error.message);
    return null;
  }

  if (!data) {
    return null;
  }

  return mapPublishedStoryRow(data as PublishedStoryRow);
}
